import { useEffect, useState } from "react"
import { accessToken } from "./config"

import type { StoryData } from "./types"

export function useStoryblok<C = any>(
  originalStoryData: StoryData<C>,
  // isPreview?: boolean
): StoryData<C> {
  const [storyState, setStoryState] = useState(originalStoryData)
  /**
   * Adds the events for updating the visual editor
   * @see https://bit.ly/3kCsqzc#initializing-the-storyblok-js-bridge
   */
  useEffect(() => {
    const { storyblok } = window
    
    /** Only for SB Editor preview mode */
    if (location === parent.location) return
    
    /** Setup storyblok context */
    storyblok.init({ accessToken })
    
    /** Load draft data in production for SB editor */
    if (process.env.NODE_ENV !== 'development') void async function () {
      const slices = location.pathname.split('?')[0].split('/').filter(s => s)
      const { getStaticProps } = await import("./data-handlers")
      const { props }: any = await getStaticProps({ preview: true, params: {
        slug: slices || undefined
      }})
      setStoryState(props.story)
    }()
    
    /** Reload on Next.js page on save or publish event in the Visual Editor */
    storyblok.on('published', location?.reload)
    
    /** Live update the story on input events */
    storyblok.on('input', async ({ story }): Promise<void> => {
      if (story.content._uid !== storyState?.content._uid) return
      story.content = storyblok.addComments(story.content, story.id)
      setStoryState(story)
    })
  }, [])

  useEffect(() => { /** Refresh data on route change */
    if (originalStoryData.uuid !== storyState.uuid) setStoryState(originalStoryData)
  }, [originalStoryData.uuid, storyState.uuid, setStoryState, originalStoryData])
  
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'development')
    console.debug('Story page data: ', storyState)
  return storyState ?? originalStoryData
}
